import React, { useEffect } from 'react';
import {
  Box,
  Typography,
  Avatar,
  Badge,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Divider,
  Button,
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  Block as BlockIcon,
  Add as AddIcon,
  Group as GroupIcon,
  NotificationsOff as MutedIcon,
} from '@mui/icons-material';
import { green, grey } from '@mui/material/colors';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import {
  setActiveConversation,
  setRightContent,
  loadMessages,
  clearUnread,
  refreshPendingRequests,
  refreshConversations,
} from '../redux/chat.redux';
import type { RightContent } from '../redux/chat.redux';
import { api } from '../api/chat';
import { formatTime, previewContent, avatarText } from '../utils/format';

interface ChatListProps {
  // 移动端:列表项点击后回调以打开右侧详情(全屏)
  onOpenDetail?: () => void;
}

// 打开一个会话(选中 + 加载消息 + 清未读 + 右侧显示聊天)
function useOpenConversation(onOpenDetail?: () => void) {
  const dispatch = useAppDispatch();
  return async (cid: number) => {
    dispatch(setActiveConversation(cid));
    dispatch(clearUnread(cid));
    dispatch(loadMessages(cid));
    dispatch(setRightContent({ type: 'chat' }));
    onOpenDetail?.();
    api.markRead(cid).catch(() => {});
  };
}

export default function ChatList({ onOpenDetail }: ChatListProps) {
  const dispatch = useAppDispatch();
  const view = useAppSelector((s) => s.chat.view);
  const conversations = useAppSelector((s) => s.chat.conversations);
  const activeId = useAppSelector((s) => s.chat.activeConversationId);
  const openConversation = useOpenConversation(onOpenDetail);

  // 统一打开详情(桌面端:右侧区域;移动端:全屏详情)
  const openDetail = (rc: RightContent) => {
    dispatch(setRightContent(rc));
    onOpenDetail?.();
  };

  useEffect(() => {
    dispatch(refreshPendingRequests());
    dispatch(refreshConversations());
    // eslint-disable-next-line
  }, [view]);

  return (
    <Box
      sx={{
        width: { xs: '100%', md: 280 },
        display: 'flex',
        flexDirection: 'column',
        borderRight: { xs: 'none', md: '1px solid rgba(0,0,0,0.12)' },
        bgcolor: '#f7f8fa',
      }}
    >
      {/* 标题栏 */}
      <Box
        sx={{
          height: 56,
          px: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid rgba(0,0,0,0.08)',
          bgcolor: '#fff',
          flexShrink: 0,
        }}
      >
        <Typography sx={{ fontWeight: 600, color: 'text.primary' }}>
          {view === 'conversations' && '会话'}
          {view === 'contacts' && '通讯录'}
          {view === 'groups' && '群聊'}
        </Typography>
        {(view === 'conversations' || view === 'groups') && (
          <Button size="small" startIcon={<AddIcon />} onClick={() => openDetail({ type: 'panel', panel: 'new-group' })}>
            新建群聊
          </Button>
        )}
      </Box>

      {/* 内容区 */}
      <Box sx={{ flexGrow: 1, overflowY: 'auto', pb: { xs: 96, md: 0 } }}>
        {view === 'conversations' && (
          <List disablePadding>
            {conversations.length === 0 && (
              <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', mt: 4 }}>
                暂无会话,去通讯录添加好友吧
              </Typography>
            )}
            {conversations.map((c) => {
              const active = c.id === activeId;
              const isGroup = c.type === 'group';
              return (
                <ListItemButton
                  key={c.id}
                  selected={active}
                  onClick={() => openConversation(c.id)}
                  sx={{
                    py: 1,
                    px: 2,
                    bgcolor: active ? 'primary.light' : 'transparent',
                    '&:hover': { bgcolor: active ? 'primary.light' : 'rgba(39,42,80,0.06)' },
                    '&.Mui-selected': { bgcolor: 'primary.light', '&:hover': { bgcolor: 'primary.light' } },
                  }}
                >
                  <ListItemAvatar sx={{ minWidth: 48 }}>
                    <Badge
                      invisible={isGroup}
                      overlap="circular"
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                      variant="dot"
                      sx={{ '& .MuiBadge-badge': { bgcolor: green[500], width: 10, height: 10 } }}
                    >
                      <Avatar src={c.display_avatar || undefined} sx={{ width: 42, height: 42, bgcolor: 'primary.main', fontSize: 18 }}>
                        {avatarText({ nickname: c.display_name, username: c.display_name })}
                      </Avatar>
                    </Badge>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box component="span" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography component="span" noWrap sx={{ fontWeight: active ? 600 : 400, maxWidth: 140 }}>
                          {c.display_name}
                        </Typography>
                        <Typography component="span" variant="caption" sx={{ color: 'text.secondary', flexShrink: 0 }}>
                          {formatTime(c.last_message?.created_at)}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Box component="span" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography component="span" noWrap variant="body2" sx={{ color: 'text.secondary', maxWidth: 150, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {c.muted_self && <MutedIcon sx={{ fontSize: 14, color: 'text.disabled' }} />}
                          {previewContent(c.last_message)}
                        </Typography>
                        {c.unread > 0 && (
                          <Box
                            component="span"
                            sx={{
                              minWidth: 18,
                              height: 18,
                              px: 0.6,
                              borderRadius: 9,
                              bgcolor: 'error.main',
                              color: '#fff',
                              fontSize: 11,
                              lineHeight: '18px',
                              textAlign: 'center',
                              flexShrink: 0,
                            }}
                          >
                            {c.unread > 99 ? '99+' : c.unread}
                          </Box>
                        )}
                      </Box>
                    }
                  />
                </ListItemButton>
              );
            })}
          </List>
        )}

        {view === 'contacts' && <ContactsView openDetail={openDetail} />}

        {view === 'groups' && (
          <GroupsView
            onOpenDetail={(groupId) => openDetail({ type: 'group-detail', groupId })}
            onCreate={() => openDetail({ type: 'panel', panel: 'new-group' })}
          />
        )}
      </Box>
    </Box>
  );
}

// ---------- 通讯录视图 ----------
function ContactsView({ openDetail }: { openDetail: (rc: RightContent) => void }) {
  const contacts = useAppSelector((s) => s.chat.contacts);
  const pendingRequests = useAppSelector((s) => s.chat.pendingRequests);

  return (
    <List disablePadding>
      <ListItemButton onClick={() => openDetail({ type: 'panel', panel: 'add-friend' })} sx={{ py: 1, px: 2 }}>
        <ListItemAvatar sx={{ minWidth: 44 }}>
          <Avatar sx={{ width: 38, height: 38, bgcolor: 'primary.main' }}>
            <PersonAddIcon fontSize="small" />
          </Avatar>
        </ListItemAvatar>
        <ListItemText primary="添加好友" primaryTypographyProps={{ fontSize: 14 }} />
      </ListItemButton>
      <ListItemButton onClick={() => openDetail({ type: 'panel', panel: 'requests' })} sx={{ py: 1, px: 2 }}>
        <ListItemAvatar sx={{ minWidth: 44 }}>
          <Avatar sx={{ width: 38, height: 38, bgcolor: 'warning.main' }}>
            <PersonAddIcon fontSize="small" />
          </Avatar>
        </ListItemAvatar>
        <ListItemText primary="新的好友" primaryTypographyProps={{ fontSize: 14 }} />
        {pendingRequests.length > 0 && (
          <Box sx={{ minWidth: 18, height: 18, px: 0.6, borderRadius: 9, bgcolor: 'error.main', color: '#fff', fontSize: 11, lineHeight: '18px', textAlign: 'center' }}>
            {pendingRequests.length}
          </Box>
        )}
      </ListItemButton>
      <ListItemButton onClick={() => openDetail({ type: 'panel', panel: 'blocked' })} sx={{ py: 1, px: 2 }}>
        <ListItemAvatar sx={{ minWidth: 44 }}>
          <Avatar sx={{ width: 38, height: 38, bgcolor: grey[500] }}>
            <BlockIcon fontSize="small" />
          </Avatar>
        </ListItemAvatar>
        <ListItemText primary="黑名单" primaryTypographyProps={{ fontSize: 14 }} />
      </ListItemButton>

      <Divider sx={{ my: 1 }} />
      <Typography variant="caption" sx={{ px: 2, color: 'text.secondary' }}>
        我的好友({contacts.length})
      </Typography>

      {contacts.length === 0 && (
        <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', mt: 3 }}>
          还没有好友,点击上方"添加好友"
        </Typography>
      )}
      {contacts.map((u) => (
        <ListItemButton key={u.id} onClick={() => openDetail({ type: 'contact-card', userId: u.id })} sx={{ py: 0.5, px: 2 }}>
          <ListItemAvatar sx={{ minWidth: 44 }}>
            <Badge
              overlap="circular"
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              variant="dot"
              sx={{ '& .MuiBadge-badge': { bgcolor: u.online ? green[500] : grey[400], width: 10, height: 10 } }}
            >
              <Avatar src={u.avatar || undefined} sx={{ width: 38, height: 38, bgcolor: 'primary.light', fontSize: 16 }}>
                {avatarText(u)}
              </Avatar>
            </Badge>
          </ListItemAvatar>
          <ListItemText
            primary={u.nickname || u.username}
            primaryTypographyProps={{ fontSize: 14 }}
            secondary={u.online ? '在线' : '离线'}
            secondaryTypographyProps={{ fontSize: 12 }}
          />
        </ListItemButton>
      ))}
    </List>
  );
}

// ---------- 群聊视图 ----------
function GroupsView(props: { onOpenDetail: (groupId: number) => void; onCreate: () => void }) {
  const groups = useAppSelector((s) => s.chat.conversations.filter((c) => c.type === 'group'));

  return (
    <List disablePadding>
      <ListItemButton onClick={props.onCreate} sx={{ py: 1, px: 2 }}>
        <ListItemAvatar sx={{ minWidth: 44 }}>
          <Avatar sx={{ width: 38, height: 38, bgcolor: 'primary.main' }}>
            <AddIcon fontSize="small" />
          </Avatar>
        </ListItemAvatar>
        <ListItemText primary="创建群聊" primaryTypographyProps={{ fontSize: 14 }} />
      </ListItemButton>
      <Divider sx={{ my: 1 }} />
      <Typography variant="caption" sx={{ px: 2, color: 'text.secondary' }}>
        我的群聊({groups.length})
      </Typography>
      {groups.length === 0 && (
        <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', mt: 3 }}>
          还没有群聊,点击"创建群聊"
        </Typography>
      )}
      {groups.map((c) => (
        <ListItemButton key={c.id} onClick={() => props.onOpenDetail(c.id)} sx={{ py: 0.5, px: 2 }}>
          <ListItemAvatar sx={{ minWidth: 44 }}>
            <Avatar src={c.display_avatar || undefined} sx={{ width: 38, height: 38, bgcolor: 'primary.light', fontSize: 16 }}>
              <GroupIcon fontSize="small" />
            </Avatar>
          </ListItemAvatar>
          <ListItemText
            primary={c.display_name}
            primaryTypographyProps={{ fontSize: 14, noWrap: true }}
            secondary={
              <Box component="span" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography component="span" noWrap variant="body2" sx={{ color: 'text.secondary', maxWidth: 160 }}>
                  {previewContent(c.last_message)}
                </Typography>
                {c.unread > 0 && (
                  <Box
                    component="span"
                    sx={{ minWidth: 18, height: 18, px: 0.6, borderRadius: 9, bgcolor: 'error.main', color: '#fff', fontSize: 11, lineHeight: '18px', textAlign: 'center' }}
                  >
                    {c.unread > 99 ? '99+' : c.unread}
                  </Box>
                )}
              </Box>
            }
          />
        </ListItemButton>
      ))}
    </List>
  );
}
